import { motion } from 'framer-motion';
import { resolveImageUrl } from '@/lib/api';

const CHAPTERS = [
  {
    n: '01',
    title: 'The Legacy',
    text: 'Founded in 1999 in the border town of Jaigaon, Mahabir Bastralaya has spent more than two decades honouring the cultural clothing of the Himalayas — one tribe, one weave, one ceremony at a time.',
  },
  {
    n: '02',
    title: 'The Communities',
    text: 'We source and craft dresses for the Tibetan, Tamang, Rai, Gurung, Mangar, Subba, Limbu, Newari, Pradhan, Lepcha and Bhutanese communities — every silhouette carrying the pride of its people.',
  },
  {
    n: '03',
    title: 'Made to Order',
    text: 'When ready-to-wear is not enough, we tailor to you. Choose your fabric, choose your fit, choose the occasion — our master tailors build the piece from the first thread onward.',
  },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };
const item = { hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } } };

export default function Manifesto({ images = [] }) {
  return (
    <section
      data-testid="manifesto-section"
      className="relative py-24 sm:py-32 lg:py-40 px-6 sm:px-10 max-w-[1600px] mx-auto"
    >
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
        className="mb-24"
      >
        <motion.p variants={item} className="eyebrow mb-6">Manifesto · Est. 1999</motion.p>
        <motion.h2 variants={item} className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
          Twenty-five years of<br/>
          <em className="text-[color:var(--mb-primary)] not-italic">Nepali heritage</em>, honoured.
        </motion.h2>
      </motion.div>

      <div className="flex flex-col gap-32">
        {CHAPTERS.map((c, idx) => {
          const img = resolveImageUrl(images[idx]);
          return (
            <motion.div
              key={c.n}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
              variants={container}
              className={`grid lg:grid-cols-12 gap-8 lg:gap-16 items-center ${idx % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}
            >
              <motion.div variants={item} className="lg:col-span-5 [direction:ltr]" data-testid={`manifesto-image-${idx}`}>
                <div className="arch-top aspect-[3/4] w-full max-w-[440px] bg-[color:var(--mb-bg-alt)] mx-auto lg:mx-0 border border-[color:var(--mb-border)] overflow-hidden flex items-center justify-center">
                  {img ? (
                    <img src={img} alt={c.title} className="max-w-full max-h-full object-contain" loading="lazy" />
                  ) : (
                    <span className="font-serif-display italic text-6xl sm:text-8xl text-[color:var(--mb-primary)]/20">{c.n}</span>
                  )}
                </div>
              </motion.div>
              <div className="lg:col-span-7 [direction:ltr]">
                <motion.div variants={item} className="chapter-numeral leading-none mb-6">{c.n}</motion.div>
                <motion.h3 variants={item} className="font-serif-display text-4xl sm:text-5xl mb-6 tracking-tight">
                  {c.title}
                </motion.h3>
                <motion.p variants={item} className="text-lg leading-relaxed text-[color:var(--mb-text-muted)] max-w-xl font-light">
                  {c.text}
                </motion.p>
                <motion.div variants={item} className="mt-8 text-[0.65rem] tracking-[0.4em] uppercase text-[color:var(--mb-text-muted)]">
                  Chapter · {c.n}
                </motion.div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
